import { useMutation, useQuery } from "@apollo/client";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, MenuItem, Select, Skeleton, TextField, Typography } from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useEffect, useRef, useState } from "react"
import {
  CreateClinicalConstraintTemplateMutation,
  CreateClinicalConstraintTemplateMutationVariables,
  CreateEpisodeMutation,
  CreateEpisodeMutationVariables,
  GetClinicalConstraintTemplatesForEpisodeQuery,
  GetClinicalConstraintTemplatesForEpisodeQueryVariables,
  GetEpisodeCareRequirementsQuery,
  GetEpisodeCareRequirementsQueryVariables,
  UpdateEpisodeMutation,
  UpdateEpisodeMutationVariables,
} from "~/graphql/__generated__/graphql"
import { useOrgConfig } from "~/providers/OrgConfigProvider"
import { Permission, useHasPermission } from "~/hooks/permissions";
import { addDays, addHours, addMinutes, format, isBefore, startOfDay } from "date-fns"
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import CareRequirementsEditor from "./CareRequirementsEditor";
// Demo-only: intake/Predict flow surfaces an equipment ETA the admit visit
// shouldn't be scheduled before.
import EquipmentEtaRefreshButton from "../common/EquipmentEtaRefreshButton";
import DatePicker from '../common/forms/DatePicker';
import { TimePicker } from "@mui/x-date-pickers";
import { setVisitType } from "../VisitTypeDuration";
import { getCareRequirementDetailedSummaryLabel } from "./careRequirementSummary";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import { useSnackbar } from "~/hooks/useSnackbar";
import { CREATE_CLINICAL_CONSTRAINT_TEMPLATE_MUTATION, GET_CLINICAL_CONSTRAINT_TEMPLATES_FOR_EPISODE_QUERY } from "~/queries/clinicalConstraintTemplate";
import { CREATE_EPISODE_MUTATION, GET_EPISODE_CARE_REQUIREMENTS_QUERY, UPDATE_EPISODE_MUTATION } from "~/queries/episode";
import { timezoneAbbreviation } from "@caredna-marlin/utils/dates";
import {
  CareRequirement,
  careRequirementFromTemplateRequirement,
  careRequirementStartDate,
  careRequirementToClinicalConstraintTemplateRequirementInput,
  cloneCareRequirement,
  createNewCareRequirement,
  getDefaultCareRequirementDateRange,
  getLengthOfStayDays,
  getLengthOfStayMaxSchedulingViolations,
  parseCareRequirementsFromGQL,
  todayInTimezone,
  unparseCareRequirementsForGQL,
  unparseCareRequirementForGQL,
  withCareRequirementsFrequency,
} from "@caredna-marlin/utils/client";
import { getEpisodeGroupLabel } from "~/utils/visitsHelper";

type Props = {
  patient: { id: number, firstName: string, lastName: string, addressTimezone: string | null, patientNotes?: string | null }
  serviceLineId: number
  organizationalUnits: { id: number, name: string, active: boolean }[]
  episodeId?: number
  reopen?: boolean
  onSkip?: (organizationalUnitId: number | null) => Promise<void> | void
  onSaved?: () => Promise<void> | void
  onClose: () => void
  // Demo-only: intake/Predict deep-links pre-fill the template and the earliest
  // admit ("After") time for a brand-new episode.
  defaultTemplateName?: string
  defaultAdmitAfterMs?: number
}

type EpisodeFrequency = CareRequirement["rruleFreq"];

type EpisodeFormState = {
  admissionDate: Date,
  expectedDischargeDate: Date,
  organizationalUnitId: number | null,
  autoschedulerGroup: number | null,
  careRequirements: CareRequirement[]
  admissionDayCareRequirements: CareRequirement[]
}

const fieldWidth = 240;
const DEFAULT_EPISODE_FREQUENCY: CareRequirement["rruleFreq"] = "daily";
const PATIENT_EPISODE_REFETCH_QUERIES = [
  "PatientDetail",
  "PatientDetailScreen",
];

type EpisodeTemplates = GetClinicalConstraintTemplatesForEpisodeQuery['clinicalConstraintTemplate'];

const isDefined = <T,>(value: T | null | undefined): value is T => value != null;

const formatDayCount = (days: number) => `${days} ${days === 1 ? "day" : "days"}`;

const getEpisodeGroupOptions = (autoschedulerGroups: number) =>
  Array.from({ length: autoschedulerGroups }, (_, index) => {
    const groupNumber = index + 1;
    return {
      value: groupNumber,
      label: getEpisodeGroupLabel(groupNumber),
    };
  });
const roundUpToNext15 = (date: Date): Date => {
  const ms = 15 * 60 * 1000;
  return new Date(Math.ceil(date.getTime() / ms) * ms);
};

const applyDefaultEpisodeFrequency = (careRequirements: CareRequirement[]) =>
  withCareRequirementsFrequency(careRequirements, DEFAULT_EPISODE_FREQUENCY);

type FormProps = Props & {
  initialEpisode: EpisodeFormState;
  templates: EpisodeTemplates;
  loadingTemplates: boolean;
  episodeActedOn: boolean;
};

const CareEpisodeDialogForm = ({ onClose, onSkip, onSaved, patient, serviceLineId, organizationalUnits, episodeId, reopen, defaultTemplateName, defaultAdmitAfterMs, initialEpisode, templates, loadingTemplates, episodeActedOn }: FormProps) => {
  const isEditing = typeof episodeId === "number";
  const orgConfig = useOrgConfig()
  const { setErrorMessage: setToastMessage } = useSnackbar();
  const hasManageOrgConfigPermission = useHasPermission(Permission.manageOrgConfig) ?? false;
  const { timeLabel } = useZonedDateFormat();
  const [episode, setEpisode] = useState<EpisodeFormState>(() => ({
    ...initialEpisode,
    careRequirements: applyDefaultEpisodeFrequency(initialEpisode.careRequirements),
    admissionDayCareRequirements: applyDefaultEpisodeFrequency(initialEpisode.admissionDayCareRequirements),
  }))
  const [selectedTemplateId, setSelectedTemplateId] = useState<number>(0);
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
  // Earliest time the day-of-admission visit(s) can be scheduled on the
  // Admission Date. When editing an existing episode, seed it from the loaded
  // first day-of requirement's start so reopening doesn't recompute it from now
  // and clobber that visit; otherwise default to now rounded up to the next 15.
  const [admitAfterTime, setAdmitAfterTime] = useState<Date | null>(() => {
    const firstDayOf = initialEpisode.admissionDayCareRequirements[0];
    if (isEditing && firstDayOf && firstDayOf.scheduleType === "timeRange") {
      return firstDayOf.minTime;
    }
    // Demo-only: intake deep-links seed the After time from the equipment ETA.
    if (defaultAdmitAfterMs != null) {
      return new Date(defaultAdmitAfterMs);
    }
    return roundUpToNext15(new Date());
  });
  // Demo-only: intake patients carry an equipment ETA we warn against
  // scheduling the admit visit before.
  const [equipmentEtaMs, setEquipmentEtaMs] = useState<number | undefined>(defaultAdmitAfterMs);
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [createEpisode, { loading: creating }] = useMutation<CreateEpisodeMutation, CreateEpisodeMutationVariables>(
    CREATE_EPISODE_MUTATION,
    {
      awaitRefetchQueries: true,
      refetchQueries: PATIENT_EPISODE_REFETCH_QUERIES,
    },
  );
  const [updateEpisode, { loading: updating }] = useMutation<UpdateEpisodeMutation, UpdateEpisodeMutationVariables>(
    UPDATE_EPISODE_MUTATION,
    {
      awaitRefetchQueries: true,
      refetchQueries: PATIENT_EPISODE_REFETCH_QUERIES,
    },
  );
  const saving = creating || updating;
  const admissionInPast = isBefore(startOfDay(episode.admissionDate), todayInTimezone(patient.addressTimezone || undefined));
  const dayOfLocked = episodeActedOn || admissionInPast;
  const lengthOfStayDays = getLengthOfStayDays(episode.admissionDate, episode.expectedDischargeDate);
  const lengthOfStayValidationMessage = (() => {
    const visitTypes = Object.values(orgConfig.visitTypes).filter(isDefined);

    // Ongoing care requirements: standard admission→discharge LOS check.
    const ongoingViolations = getLengthOfStayMaxSchedulingViolations({
      admissionDate: episode.admissionDate,
      expectedDischargeDate: episode.expectedDischargeDate,
      careRequirements: episode.careRequirements,
      visitTypes,
    });

    // Day-of-admission care requirements: the relevant horizon is today →
    // admissionDate (how far out we're trying to pre-schedule), not the
    // full LOS. We only flag violations when the admission date is so far
    // in the future that the visit type's max scheduling window can't reach.
    const todayStart = todayInTimezone(patient.addressTimezone || undefined);
    const dayOfViolations = isBefore(todayStart, startOfDay(episode.admissionDate))
      ? getLengthOfStayMaxSchedulingViolations({
          admissionDate: todayStart,
          expectedDischargeDate: episode.admissionDate,
          careRequirements: episode.admissionDayCareRequirements,
          visitTypes,
        })
      : [];

    const messages: string[] = [];
    if (ongoingViolations.length > 0) {
      messages.push(`Length of stay is ${formatDayCount(lengthOfStayDays)}, which exceeds max scheduling for: ${ongoingViolations
        .map(({ visitTypeId, visitTypeName, maxSchedulingDays }) =>
          `${visitTypeName ?? `Visit Type ${visitTypeId}`} (${formatDayCount(maxSchedulingDays)} max)`
        )
        .join(", ")}.`);
    }
    if (dayOfViolations.length > 0) {
      const daysUntilAdmission = getLengthOfStayDays(todayStart, episode.admissionDate);
      messages.push(`Admission date is ${formatDayCount(daysUntilAdmission)} out, which exceeds max scheduling for day-of-admission: ${dayOfViolations
        .map(({ visitTypeId, visitTypeName, maxSchedulingDays }) =>
          `${visitTypeName ?? `Visit Type ${visitTypeId}`} (${formatDayCount(maxSchedulingDays)} max)`
        )
        .join(", ")}.`);
    }

    return messages.length > 0 ? messages.join(" ") : null;
  })();
  const hasLengthOfStayMaxSchedulingViolation = lengthOfStayValidationMessage !== null;
  const firstCareRequirementIsRelative = episode.careRequirements.length > 0
    && episode.careRequirements[0].scheduleType === "previousVisitRelative";
  const firstCareRequirementValidationMessage = firstCareRequirementIsRelative
    ? "First care requirement must have a time range"
    : null;
  // The first day-of-admission visit can't start before the "After" time — that
  // field is its earliest allowed start.
  const firstDayOfRequirement = episode.admissionDayCareRequirements[0];
  const firstDayOfStartsBeforeAfter = !dayOfLocked
    && admitAfterTime != null
    && firstDayOfRequirement != null
    && firstDayOfRequirement.scheduleType === "timeRange"
    && firstDayOfRequirement.minTime.getTime() < admitAfterTime.getTime();
  const firstDayOfValidationMessage = firstDayOfStartsBeforeAfter
    ? "The first day-of-admission visit can't start before the After time."
    : null;
  const dischargeBeforeAdmission = isBefore(
    startOfDay(episode.expectedDischargeDate),
    startOfDay(episode.admissionDate),
  );
  // The expected discharge date is always forward-looking — it can't be in the
  // past regardless of acted-on state. The picker's floor (and this guard) is the
  // later of today and the admission date, so it also can't precede admission.
  const dischargeInPast = isBefore(
    startOfDay(episode.expectedDischargeDate),
    todayInTimezone(patient.addressTimezone || undefined),
  );
  const dischargeMinDate = admissionInPast
    ? todayInTimezone(patient.addressTimezone || undefined)
    : startOfDay(episode.admissionDate);
  const dischargeValidationMessage = dischargeBeforeAdmission
    ? "Expected discharge date can't be before the admission date."
    : dischargeInPast
      ? "The expected discharge date can't be in the past."
      : null;
  // A past admission date can't be scheduled, so it blocks save whenever the date
  // is still editable — i.e. the episode hasn't been acted on. (An acted-on
  // episode's date is locked at its stored value and never gates ongoing edits.)
  const admissionDateInvalid = admissionInPast && !episodeActedOn;
  const admissionDateValidationMessage = admissionDateInvalid
    ? "The admission date can't be in the past"
    : null;
  const templateDurationDays = Math.max(1, lengthOfStayDays);

  const serviceLine = orgConfig.serviceLines[serviceLineId]
  if (!serviceLine) { return null }

  const autoschedulerGroups = serviceLine.schedulingConfig.autoschedulerGroups;
  const episodeGroupOptions = getEpisodeGroupOptions(autoschedulerGroups);
  const showGroupSelector = serviceLine.schedulingConfig.useAutoscheduler && autoschedulerGroups > 0;
  const organizationalUnitOptions = organizationalUnits.filter((organizationalUnit) =>
    organizationalUnit.active || organizationalUnit.id === episode.organizationalUnitId
  );
  const selectedOrganizationalUnit = organizationalUnits.find((organizationalUnit) =>
    organizationalUnit.id === episode.organizationalUnitId
  );
  const showOrganizationalUnitSelector = organizationalUnitOptions.length > 1
    || selectedOrganizationalUnit?.active === false;
  const applyTemplate = (templateId: number) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) { return; }

    const nextFreq: EpisodeFrequency = DEFAULT_EPISODE_FREQUENCY;

    setEpisode((prev) => {
      const tz = patient.addressTimezone || undefined;
      // For new episodes derive expected discharge date from the template's
      // durationDays but do not blindly modifiy it for in progress episodes
      const expectedDischargeDate = !dayOfLocked && template.durationDays
        ? startOfDay(addDays(prev.admissionDate, template.durationDays))
        : prev.expectedDischargeDate;

      const mapped: CareRequirement[] = (template.requirements ?? []).map((req) => ({
        ...careRequirementFromTemplateRequirement({
          requirement: req,
          frequency: nextFreq,
          admissionDate: prev.admissionDate,
          expectedDischargeDate,
          timezone: tz,
        }),
        // We never carry template requirement ids over to patient care requirements.
        id: undefined,
        // Ongoing requirements start the day after admission.
        dtstart: careRequirementStartDate(prev.admissionDate, false),
      }));

      return {
        ...prev,
        expectedDischargeDate,
        careRequirements: mapped,
        // Never rebuild the day-of visits while the day-of section is locked —
        // the user can't see or edit them, and silently replacing them would
        // orphan the existing day-of requirement records on save.
        admissionDayCareRequirements: dayOfLocked
          ? prev.admissionDayCareRequirements
          : admitAfterTime != null
            ? buildDayOfAdmissionRequirements(prev.admissionDate, admitAfterTime, template)
            : prev.admissionDayCareRequirements,
      };
    });
  };

  // Build the auto-populated Day of Admission requirements:
  //  - the template's designated first / start-of-care visit (time-range,
  //    start = After time, end = After + 2h, default services/duration), and
  //  - any template visits that would typically fall more than 3 hours after the
  //    first visit's start (time-range template visits only).
  const buildDayOfAdmissionRequirements = (
    admissionDate: Date,
    afterTime: Date,
    template: EpisodeTemplates[number] | undefined,
  ): CareRequirement[] => {
    const result: CareRequirement[] = [];
    const dayStart = startOfDay(admissionDate);

    // The first / start-of-care visit type is an explicit property of the
    // clinical template (firstVisitTypeId). No template, or "none", means no
    // first visit is auto-seeded — it is never guessed from the visit type name.
    const firstVisitTypeId = template?.firstVisitTypeId ?? null;
    if (firstVisitTypeId != null) {
      const base = createNewCareRequirement({
        frequency: DEFAULT_EPISODE_FREQUENCY,
        admissionDate,
        expectedDischargeDate: admissionDate,
      });
      const withVisitType = { ...base, ...setVisitType(String(firstVisitTypeId), base, orgConfig) };
      // Apply the template's saved first-visit services/duration on top of the
      // visit type's defaults, mirroring what was captured when the template
      // was created from an episode. Falls back to the visit type defaults when
      // the template didn't record them (older templates).
      const savedServiceIds = template?.firstVisitServiceIds ?? null;
      const savedDuration = template?.firstVisitDuration ?? null;
      const firstVisitType = orgConfig.visitTypes[String(firstVisitTypeId)];
      const withDefaults = {
        ...withVisitType,
        ...(savedServiceIds != null ? {
          serviceIds: savedServiceIds.map(String),
          serviceNames: savedServiceIds.map((sid) =>
            firstVisitType?.visitTypeServices.find((s) => s.service?.id === sid)?.service?.name ?? ""),
        } : {}),
        ...(savedDuration != null ? {
          duration: String(savedDuration),
          durationLocked: true,
        } : {}),
      };
      result.push({
        ...withDefaults,
        scheduleType: "timeRange",
        minTime: afterTime,
        maxTime: addHours(afterTime, 2),
        dtstart: dayStart,
        until: dayStart,
        dayOfAdmission: true,
      });
    }

    if (template) {
      const tz = patient.addressTimezone || undefined;
      // Work in the patient's local clock: the "After" instant's minutes-of-day
      // and the template's HH:mm windows are all patient-timezone wall-clock.
      const afterZoned = tz ? toZonedTime(afterTime, tz) : afterTime;
      const afterMin = afterZoned.getHours() * 60 + afterZoned.getMinutes();
      // Build an instant whose patient-timezone wall-clock is `mins` after
      // midnight on the admission day (mirrors how persisted day-of times parse).
      const minutesToInstant = (mins: number): Date => {
        const wall = addMinutes(dayStart, mins);
        return tz ? fromZonedTime(wall, tz) : wall;
      };
      const reqs = template.requirements ?? [];
      const hhmmToMin = (s: string) => {
        const [h, m] = s.split(":").map(Number);
        return h * 60 + m;
      };

      // Derive each visit's clock window by chaining through the template: the
      // first visit must have an explicit time range; "relative to last visit"
      // visits are offset from the prior visit's derived window.
      let prevStart: number | null = null;
      let prevEnd: number | null = null;
      const derived = reqs.map((req) => {
        let startMin: number | null = null;
        let endMin: number | null = null;
        const hasAbsolute = req.earliestStartTime != null || req.latestStartTime != null;
        const hasRelative = req.minTimeFromLastVisit != null || req.maxTimeFromLastVisit != null;

        if (hasAbsolute) {
          startMin = req.earliestStartTime != null ? hhmmToMin(req.earliestStartTime) : null;
          endMin = req.latestStartTime != null ? hhmmToMin(req.latestStartTime) : null;
        } else if (hasRelative && (prevStart != null || prevEnd != null)) {
          const startOffset = req.minTimeFromLastVisit ?? req.maxTimeFromLastVisit;
          const endOffset = req.maxTimeFromLastVisit ?? req.minTimeFromLastVisit;
          const anchorStart = prevStart ?? prevEnd;
          const anchorEnd = prevEnd ?? prevStart;
          startMin = anchorStart != null && startOffset != null ? anchorStart + startOffset : null;
          endMin = anchorEnd != null && endOffset != null ? anchorEnd + endOffset : null;
        }

        if (startMin != null || endMin != null) {
          prevStart = startMin ?? endMin;
          prevEnd = endMin ?? startMin;
        }
        return { req, startMin, endMin };
      });

      // Only proceed when the first visit anchors the chain with an explicit time.
      if (derived[0]?.startMin != null) {
        for (const { req, startMin, endMin } of derived) {
          if (startMin == null || startMin - afterMin <= 180) { continue; }
          const cr = careRequirementFromTemplateRequirement({
            requirement: req,
            frequency: DEFAULT_EPISODE_FREQUENCY,
            admissionDate,
            expectedDischargeDate: admissionDate,
            timezone: tz,
          });
          const minTime = minutesToInstant(startMin);
          const maxTime = minutesToInstant(endMin != null ? endMin : startMin + 120);
          result.push({
            ...cr,
            id: undefined,
            scheduleType: "timeRange",
            minTime,
            maxTime,
            dtstart: dayStart,
            until: dayStart,
            dayOfAdmission: true,
          });
        }
      }
    }

    return result;
  };

  // Recompute the Day of Admission section from the inputs that seed it.
  // With a template selected, the section is fully derived, so any change to
  // the template, admission date, or "after" time rebuilds it. With no
  // template, there are no derived defaults to regenerate: we preserve the
  // user's / loaded episode's rows and only pin the first (start-of-care)
  // visit's start to the "after" time. This runs from the field onChange
  // handlers (not an effect) so mounting an existing episode never clobbers
  // its loaded rows.
  const recomputeDayOfAdmission = (
    prev: EpisodeFormState,
    admissionDate: Date,
    afterTime: Date | null,
  ): CareRequirement[] => {
    if (selectedTemplate && afterTime != null) {
      return buildDayOfAdmissionRequirements(admissionDate, afterTime, selectedTemplate);
    }
    return prev.admissionDayCareRequirements.map((cr, index) => {
      const shifted = {
        ...cr,
        dtstart: careRequirementStartDate(admissionDate, true),
        until: startOfDay(admissionDate),
      };
      if (index === 0 && afterTime != null && shifted.scheduleType === "timeRange") {
        const maxTime = shifted.maxTime.getTime() > afterTime.getTime() ? shifted.maxTime : addHours(afterTime, 2);
        return { ...shifted, minTime: afterTime, maxTime };
      }
      return shifted;
    });
  };

  // Demo-only: intake/Predict deep-links arrive with a template name. Once
  // templates have loaded, auto-select the matching template for a brand-new
  // episode so the Day of Admission section is pre-populated (applyTemplate
  // seeds it from the already-seeded `admitAfterTime`). Runs once.
  const appliedIntakeDefaultsRef = useRef(false);
  useEffect(() => {
    if (appliedIntakeDefaultsRef.current) { return; }
    if (isEditing || !defaultTemplateName) { return; }
    if (templates.length === 0) { return; }
    const match = templates.find((t) => t.name === defaultTemplateName);
    if (!match) { return; }
    appliedIntakeDefaultsRef.current = true;
    setSelectedTemplateId(match.id);
    applyTemplate(match.id);
  }, [templates, defaultTemplateName, isEditing]);

  const onSave = async () => {
    if (
      hasLengthOfStayMaxSchedulingViolation
      || firstCareRequirementIsRelative
      || firstDayOfStartsBeforeAfter
      || dischargeBeforeAdmission
      || dischargeInPast
      || admissionDateInvalid
      || (showGroupSelector && episode.autoschedulerGroup == null)
    ) { return; }

    const tz = patient.addressTimezone || undefined;
    // Pin day-of-admission care requirements to a single-day window and merge
    // them in front of the ongoing requirements so the autoscheduler sees them
    // first.
    //
    // We keep day-of requirements in the payload even when the admission date is
    // already in the past (e.g. editing the ongoing schedule mid-stay).
    // updateEpisode reconciles the submitted set against what's stored and
    // soft-deletes anything omitted, so dropping them here would destroy the
    // record that an admit visit was required and orphan the already-created
    // day-of visit. Keeping them is safe because a mid-episode regenerate runs
    // with the window floored at tomorrow (includeToday is false once any visit
    // has started), so a past-dated day-of requirement falls outside the window
    // and never (re)generates a visit on an elapsed day.
    //
    // Stamp the dayOfAdmission flag from the section each requirement lives in,
    // so classification is explicit rather than inferred from the date window.
    const admissionDayWithDates = episode.admissionDayCareRequirements.map((cr) => ({
      ...cr,
      dayOfAdmission: true,
      dtstart: startOfDay(episode.admissionDate),
      until: startOfDay(episode.admissionDate),
    }));
    const ongoing = episode.careRequirements.map((cr) => ({ ...cr, dayOfAdmission: false }));
    const combined = applyDefaultEpisodeFrequency([
      ...admissionDayWithDates,
      ...ongoing,
    ]);

    try {
      if (isEditing) {
        if (episodeId === undefined) { return; }
        const careRequirements = unparseCareRequirementsForGQL(combined, tz);

        await updateEpisode({
          variables: {
            episodeId,
            organizationalUnitId: episode.organizationalUnitId,
            active: reopen ? true : null,
            autoschedulerGroup: episode.autoschedulerGroup,
            careRequirements,
            precautionIds: null,
          },
          awaitRefetchQueries: true,
          refetchQueries: "active",
        });
      } else {
        const careRequirements = combined.map((careRequirement) =>
          unparseCareRequirementForGQL(careRequirement, tz)
        );

        await createEpisode({
          variables: {
            patientId: patient.id,
            serviceLineId,
            organizationalUnitId: episode.organizationalUnitId,
            autoschedulerGroup: episode.autoschedulerGroup,
            careRequirements,
            precautionIds: null,
          },
          awaitRefetchQueries: true,
          refetchQueries: "active",
        });
      }
    } catch (error) {
      console.error(error);
      setToastMessage("Auto-scheduling failed. Please try again.");
      return;
    }

    await onSaved?.();
    if (!onSaved) {
      onClose();
    }
  };

  const hasInvalidRows =
    episode.careRequirements.some((careRequirement) => !careRequirement.visitTypeId) ||
    (!dayOfLocked && episode.admissionDayCareRequirements.some((careRequirement) => !careRequirement.visitTypeId));
  const groupUnset = showGroupSelector && episode.autoschedulerGroup == null;
  const missingOrganizationalUnit = !isEditing && organizationalUnitOptions.length > 0 && episode.organizationalUnitId == null;
  const saveDisabled = saving || hasInvalidRows || hasLengthOfStayMaxSchedulingViolation || firstCareRequirementIsRelative || firstDayOfStartsBeforeAfter || dischargeBeforeAdmission || dischargeInPast || admissionDateInvalid || groupUnset || missingOrganizationalUnit;
  const timezone = patient.addressTimezone ?? undefined;
  const canSkip = !isEditing && !!onSkip;

  const ongoingEffectiveDayLabel = format(addDays(todayInTimezone(timezone), 1), "EEEE, MMMM d");

  const handleSkip = async () => {
    if (!onSkip) { return; }
    setSkipping(true);
    try {
      await onSkip(episode.organizationalUnitId);
    } finally {
      setSkipping(false);
    }
  };

  return <Dialog
    open={true}
    onClose={onClose}
    fullWidth
    maxWidth="lg"
    PaperProps={{ sx: { borderRadius: 2, minHeight: 462 } }}
  >
    <DialogTitle sx={{ px: 3, pt: 2, pb: 1 }}>
      {serviceLine.name} Episode for {patient.firstName} {patient.lastName}
    </DialogTitle>

    {/* Demo-only: surface intake notes carried over from the Predict flow. */}
    {patient.patientNotes?.trim() && (
      <Box sx={{ px: 3, pb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", whiteSpace: "pre-wrap" }}>
          {patient.patientNotes}
        </Typography>
      </Box>
    )}

    <DialogContent dividers sx={{ px: 3, py: 2, height: 620 }}>
      <Box sx={{ mb: 2, display: "flex", flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle2">Template</Typography>
          <FormControl variant="standard" sx={{ width: fieldWidth }}>
            <Select
              value={selectedTemplateId}
              onChange={(e) => {
                const templateId = Number(e.target.value);
                if (!Number.isFinite(templateId)) { return; }
                setSelectedTemplateId(templateId);
                if (templateId !== 0) {
                  applyTemplate(templateId);
                }
              }}
              fullWidth
              size="small"
              displayEmpty
              renderValue={(selected) => {
                if (selected === 0) {
                  return (
                    <Box component="span" sx={{ color: "text.disabled" }}>
                      {loadingTemplates ? "Loading templates…" : "Select template"}
                    </Box>
                  );
                }
                const selectedTemplate = templates.find((t) => t.id === selected);
                return selectedTemplate?.name ?? selected;
              }}
            >
              <MenuItem value={0}>
                <em>None</em>
              </MenuItem>
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {showGroupSelector && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2">Group</Typography>
            <FormControl variant="standard" sx={{ width: fieldWidth }}>
              <Select
                displayEmpty
                value={episode.autoschedulerGroup?.toString() ?? ""}
                renderValue={(selected) => {
                  if (!selected) {
                    return "";
                  }
                  const option = episodeGroupOptions.find(({ value }) => value.toString() === selected);
                  return option?.label ?? "";
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  setEpisode((prev) => ({
                    ...prev,
                    autoschedulerGroup: value === "" ? null : Number(value),
                  }));
                }}
                fullWidth
                size="small"
              >
                <MenuItem value="">
                  <em>&nbsp;</em>
                </MenuItem>
                {episodeGroupOptions.map(({ value, label }) => (
                  <MenuItem key={value} value={value.toString()}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        {showOrganizationalUnitSelector && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2">Organizational unit</Typography>
            <FormControl variant="standard" sx={{ width: fieldWidth }}>
              <Select
                displayEmpty
                value={episode.organizationalUnitId?.toString() ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setEpisode((prev) => ({
                    ...prev,
                    organizationalUnitId: value === "" ? null : Number(value),
                  }));
                }}
                fullWidth
                size="small"
              >
                <MenuItem value="" disabled={initialEpisode.organizationalUnitId != null}>
                  <em>Select organizational unit</em>
                </MenuItem>
                {organizationalUnitOptions.map((organizationalUnit) => (
                  <MenuItem key={organizationalUnit.id} value={organizationalUnit.id.toString()}>
                    {organizationalUnit.name}{organizationalUnit.active ? "" : " (retired)"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>
      {lengthOfStayValidationMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {lengthOfStayValidationMessage}
        </Alert>
      )}
      {firstCareRequirementValidationMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {firstCareRequirementValidationMessage}
        </Alert>
      )}
      {firstDayOfValidationMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {firstDayOfValidationMessage}
        </Alert>
      )}
      {dischargeValidationMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {dischargeValidationMessage}
        </Alert>
      )}
      {admissionDateValidationMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {admissionDateValidationMessage}
        </Alert>
      )}
      <Box>
        <Accordion defaultExpanded disableGutters square sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 44, "&.Mui-expanded": { minHeight: 44 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Day of Admission</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pt: 0 }}>
            <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <Box sx={{ width: fieldWidth }}>
                <DatePicker
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                  formatDensity="dense"
                  disabled={episodeActedOn}
                  minDate={episodeActedOn ? undefined : todayInTimezone(timezone)}
                  value={episode.admissionDate}
                  onChange={(admissionDate) => admissionDate && setEpisode((prev) => ({
                    ...prev,
                    admissionDate,
                    careRequirements: prev.careRequirements.map((careRequirement) => ({
                      ...careRequirement,
                      dtstart: careRequirementStartDate(admissionDate, false),
                    })),
                    admissionDayCareRequirements: recomputeDayOfAdmission(prev, admissionDate, admitAfterTime),
                  }))}
                />
              </Box>
              {!dayOfLocked && (
                <>
                  <Typography variant="body2">after</Typography>
                  <Box sx={{ width: fieldWidth }}>
                    <TimePicker
                      slotProps={{ textField: { size: "small", fullWidth: true } }}
                      value={admitAfterTime ? (timezone ? toZonedTime(admitAfterTime, timezone) : admitAfterTime) : null}
                      onChange={(value) => {
                        const afterTime = value ? (timezone ? fromZonedTime(value, timezone) : value) : null;
                        setAdmitAfterTime(afterTime);
                        setEpisode((prev) => ({
                          ...prev,
                          admissionDayCareRequirements: recomputeDayOfAdmission(prev, prev.admissionDate, afterTime),
                        }));
                      }}
                    />
                  </Box>
                  {timezone && (
                    <Typography variant="body2" color="text.secondary">
                      {timezoneAbbreviation(timezone)}
                    </Typography>
                  )}
                </>
              )}
            </Box>
            {/* Demo-only: warn when the admit After time is earlier than the
                intake equipment ETA. Compared in the patient's local clock. */}
            {!dayOfLocked && equipmentEtaMs != null && admitAfterTime != null && (() => {
              const eta = new Date(equipmentEtaMs);
              const afterZoned = timezone ? toZonedTime(admitAfterTime, timezone) : admitAfterTime;
              const etaZoned = timezone ? toZonedTime(eta, timezone) : eta;
              const afterMin = afterZoned.getHours() * 60 + afterZoned.getMinutes();
              const etaMin = etaZoned.getHours() * 60 + etaZoned.getMinutes();
              if (afterMin >= etaMin) { return null; }
              return (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                    <span>
                      {format(afterZoned, "h:mm a")} is earlier than the equipment ETA of {format(etaZoned, "h:mm a")}
                    </span>
                    <EquipmentEtaRefreshButton etaMs={equipmentEtaMs} onRefreshed={setEquipmentEtaMs} />
                  </Box>
                </Alert>
              );
            })()}
            {dayOfLocked ? (
              <Alert severity="info" sx={{ mb: 1 }}>
                {episodeActedOn
                  ? "This episode already has visits in progress. Day of admission plan can't be added or modified."
                  : "The admission date is in the past. Day of admission plan can't be added or modified."}
              </Alert>
            ) : (
            <CareRequirementsEditor
              careRequirements={episode.admissionDayCareRequirements}
              emptyLabel="No visits. Day-of-admission visits are scheduled on the admission date."
              setCareRequirements={(updater) => {
                setEpisode((prev) => ({
                  ...prev,
                  admissionDayCareRequirements: applyDefaultEpisodeFrequency(updater(prev.admissionDayCareRequirements)),
                }));
              }}
              serviceLineId={serviceLineId}
              timezone={patient.addressTimezone || undefined}
              createNewRequirement={() => {
                const base = createNewCareRequirement({
                  frequency: DEFAULT_EPISODE_FREQUENCY,
                  admissionDate: episode.admissionDate,
                  expectedDischargeDate: episode.admissionDate,
                });
                return {
                  ...base,
                  dtstart: startOfDay(episode.admissionDate),
                  until: startOfDay(episode.admissionDate),
                  dayOfAdmission: true,
                };
              }}
              duplicateRequirement={(source) => cloneCareRequirement(source, {
                dtstart: startOfDay(episode.admissionDate),
                until: startOfDay(episode.admissionDate),
                rruleFreq: DEFAULT_EPISODE_FREQUENCY,
                rruleInterval: null,
                rruleByweekday: null,
                dayOfAdmission: true,
              })}
              summaryLabel={(careRequirement, index) => getCareRequirementDetailedSummaryLabel({
                careRequirement,
                index,
                visitTypeName: orgConfig.visitTypes[careRequirement.visitTypeId]?.name,
                timeLabel,
                timezone,
              })}
              addButtonLabel="+ Add Visit"
              mode="openLastAdded"
              contentPaddingX={1}
            />
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded disableGutters square>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 44, "&.Mui-expanded": { minHeight: 44 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Ongoing Through Expected Discharge Date</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pt: 0 }}>
            <Box sx={{ mb: 1.5, width: fieldWidth }}>
              <DatePicker
                slotProps={{ textField: { size: "small", fullWidth: true } }}
                formatDensity="dense"
                minDate={dischargeMinDate}
                value={episode.expectedDischargeDate}
                onChange={(expectedDischargeDate) => expectedDischargeDate && setEpisode((prev) => ({
                  ...prev,
                  expectedDischargeDate,
                  careRequirements: prev.careRequirements.map((careRequirement) => ({
                    ...careRequirement,
                    until: expectedDischargeDate,
                  })),
                }))}
              />
            </Box>
            {episodeActedOn && admissionInPast && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Changes to ongoing visits take effect starting {ongoingEffectiveDayLabel}. Visits already scheduled for today will need to be adjusted manually.
              </Alert>
            )}
            <CareRequirementsEditor
              careRequirements={episode.careRequirements}
              emptyLabel="No visits. Ongoing visits are scheduled the day after admission through the expected discharge date. Discharge visits are not included."
              setCareRequirements={(updater) => {
                setEpisode((prev) => ({
                  ...prev,
                  careRequirements: applyDefaultEpisodeFrequency(updater(prev.careRequirements)),
                }));
              }}
              serviceLineId={serviceLineId}
              timezone={patient.addressTimezone || undefined}
              createNewRequirement={() => ({
                ...createNewCareRequirement({
                  frequency: DEFAULT_EPISODE_FREQUENCY,
                  admissionDate: episode.admissionDate,
                  expectedDischargeDate: episode.expectedDischargeDate,
                }),
                // Ongoing requirements start the day after admission.
                dtstart: careRequirementStartDate(episode.admissionDate, false),
              })}
              duplicateRequirement={(source) => cloneCareRequirement(source, {
                dtstart: careRequirementStartDate(episode.admissionDate, false),
                until: episode.expectedDischargeDate,
                rruleFreq: DEFAULT_EPISODE_FREQUENCY,
                rruleInterval: null,
                rruleByweekday: null,
              })}
              summaryLabel={(careRequirement, index) => getCareRequirementDetailedSummaryLabel({
                careRequirement,
                index,
                visitTypeName: orgConfig.visitTypes[careRequirement.visitTypeId]?.name,
                timeLabel,
                timezone,
              })}
              mode="openLastAdded"
              contentPaddingX={1}
            />
          </AccordionDetails>
        </Accordion>
      </Box>
    </DialogContent>

    <DialogActions sx={{ px: 3, py: 2, flexWrap: "wrap", columnGap: 1, rowGap: 1 }}>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mr: "auto" }}>
        {hasManageOrgConfigPermission ? (
          <Button
            variant="outlined"
            onClick={() => setSaveAsTemplateOpen(true)}
            disabled={saving || hasInvalidRows || episode.careRequirements.length === 0 || creatingTemplate || firstCareRequirementIsRelative}
            loading={creatingTemplate}
          >
            Save as Template
          </Button>
        ) : null}
        {canSkip && (
          <Button
            onClick={handleSkip}
            disabled={saving || skipping || missingOrganizationalUnit}
            loading={skipping}
          >
            Skip for now
          </Button>
        )}
      </Box>
      <Button onClick={onClose}>
        Cancel
      </Button>
      <Button
        onClick={onSave}
        variant="contained"
        disabled={saveDisabled}
        loading={saving}
      >
        Schedule
      </Button>
    </DialogActions>

    <SaveAsTemplateDialog
      open={saveAsTemplateOpen}
      onClose={() => setSaveAsTemplateOpen(false)}
      serviceLineId={serviceLineId}
      frequency={DEFAULT_EPISODE_FREQUENCY}
      durationDays={templateDurationDays}
      careRequirements={episode.careRequirements}
      firstVisitTypeId={episode.admissionDayCareRequirements[0]?.visitTypeId
        ? Number(episode.admissionDayCareRequirements[0].visitTypeId)
        : null}
      firstVisitServiceIds={episode.admissionDayCareRequirements[0]
        ? episode.admissionDayCareRequirements[0].serviceIds.map(Number)
        : null}
      firstVisitDuration={episode.admissionDayCareRequirements[0]
        ? (Number.parseInt(episode.admissionDayCareRequirements[0].duration, 10) || null)
        : null}
      admissionDayVisitCount={episode.admissionDayCareRequirements.length}
      timezone={patient.addressTimezone || undefined}
      onSaved={(newId) => {
        setSaveAsTemplateOpen(false);
        setCreatingTemplate(false);
        if (newId) {
          setSelectedTemplateId(newId);
        }
      }}
      onSavingTemplate={(isSaving) => setCreatingTemplate(isSaving)}
    />
  </Dialog>
}

type SaveAsTemplateDialogProps = {
  open: boolean;
  onClose: () => void;
  serviceLineId: number;
  frequency: EpisodeFrequency;
  durationDays: number;
  careRequirements: CareRequirement[];
  firstVisitTypeId: number | null;
  firstVisitServiceIds: number[] | null;
  firstVisitDuration: number | null;
  admissionDayVisitCount: number;
  timezone?: string;
  onSaved: (newId?: number) => void;
  onSavingTemplate: (isSaving: boolean) => void;
};

const SaveAsTemplateDialog = ({
  open,
  onClose,
  serviceLineId,
  frequency,
  durationDays,
  careRequirements,
  firstVisitTypeId,
  firstVisitServiceIds,
  firstVisitDuration,
  admissionDayVisitCount,
  timezone,
  onSaved,
  onSavingTemplate,
}: SaveAsTemplateDialogProps) => {
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const { setErrorMessage: setToastMessage } = useSnackbar();
  const [createClinicalConstraintTemplate, { loading: creatingTemplate }] = useMutation<
    CreateClinicalConstraintTemplateMutation,
    CreateClinicalConstraintTemplateMutationVariables
  >(CREATE_CLINICAL_CONSTRAINT_TEMPLATE_MUTATION);

  const close = () => {
    setTemplateName("");
    setTemplateDescription("");
    onClose();
  };

  const onSaveAsTemplate = async () => {
    const name = templateName.trim();
    if (!name || careRequirements.length === 0) { return; }

    const requirements = careRequirements.map((cr) =>
      careRequirementToClinicalConstraintTemplateRequirementInput(cr, timezone)
    );

    try {
      onSavingTemplate(true);
      const result = await createClinicalConstraintTemplate({
        variables: {
          serviceLineId,
          name,
          description: templateDescription.trim() || null,
          rruleFreq: frequency,
          durationDays,
          firstVisitTypeId,
          firstVisitServiceIds,
          firstVisitDuration,
          requirements,
        },
        awaitRefetchQueries: true,
        refetchQueries: ["GetClinicalConstraintTemplatesForEpisode", "GetClinicalConstraintTemplatesForConfig"],
      });

      const newId = result.data?.createClinicalConstraintTemplate?.id;
      onSaved(newId ?? undefined);
    } catch (error) {
      console.error(error);
      onSavingTemplate(false);
      setToastMessage("Couldn't save the template. Please try again.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth={false}
      PaperProps={{ sx: { width: "100%", maxWidth: 780 } }}
    >
      <DialogTitle>
        Save as Clinical Template
      </DialogTitle>
      <DialogContent dividers>
        {admissionDayVisitCount > 1 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Only the first visit on the day of admission will be saved to the template. Other visits on the day of admission will be derived from the template's ongoing visits based on the time of the first visit.
          </Alert>
        )}
        <TextField
          label="Template name"
          size="small"
          fullWidth
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
        />
        <TextField
          label="Description (optional)"
          size="small"
          fullWidth
          sx={{ mt: 2 }}
          value={templateDescription}
          onChange={(e) => setTemplateDescription(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={close}
          disabled={creatingTemplate}
        >
          Cancel
        </Button>
        <Button
          onClick={onSaveAsTemplate}
          variant="contained"
          disabled={!templateName.trim() || creatingTemplate}
          loading={creatingTemplate}
        >
          Save Template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CareEpisodeDialog = ({ onClose, onSkip, onSaved, patient, serviceLineId, organizationalUnits, episodeId, reopen, defaultTemplateName, defaultAdmitAfterMs }: Props) => {
  const isEditing = typeof episodeId === "number";
  const orgConfig = useOrgConfig();
  const serviceLine = orgConfig.serviceLines[serviceLineId];

  const episodeIdOrZero = episodeId ?? 0;
  const { data, loading, error, refetch } = useQuery<GetEpisodeCareRequirementsQuery, GetEpisodeCareRequirementsQueryVariables>(
    GET_EPISODE_CARE_REQUIREMENTS_QUERY,
    {
      variables: { episodeId: episodeIdOrZero },
      skip: !isEditing,
      // network-only (not cache-and-network): this query also carries the
      // "acted on" flag that locks the day-of section, which must reflect the
      // current visit statuses. Reading the cache first would briefly render the
      // stale lock (e.g. right after a status change) before the network corrects
      // it — showLoading holds the skeleton until this resolves.
      fetchPolicy: "network-only",
    },
  );
  const { data: templatesData, loading: loadingTemplates } = useQuery<
    GetClinicalConstraintTemplatesForEpisodeQuery,
    GetClinicalConstraintTemplatesForEpisodeQueryVariables
  >(
    GET_CLINICAL_CONSTRAINT_TEMPLATES_FOR_EPISODE_QUERY,
    {
      variables: { serviceLineId },
      fetchPolicy: "cache-and-network",
    },
  );

  if (!serviceLine) { return null; }

  const templates = templatesData?.clinicalConstraintTemplate ?? [];
  const hasTemplatesData = Boolean(templatesData);
  const existingCareRequirements = isEditing ? data?.careRequirement : undefined;
  const existingEpisode = isEditing ? data?.episodeSingle : undefined;
  const episodeDataReady = isEditing ? !!existingCareRequirements && !!existingEpisode : true;
  const episodeError = isEditing ? error : undefined;
  // Any visit past scheduled/canceled → episode has been acted on (day-of lock).
  const episodeActedOn = (data?.visit?.length ?? 0) > 0;

  const showLoading = !episodeDataReady || (loadingTemplates && !hasTemplatesData);

  if (showLoading) {
    return (
      <Dialog
        open={true}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 2, minHeight: 350 } }}
      >
        <DialogTitle sx={{ px: 3, pt: 2, pb: 1 }}>
          {serviceLine.name} Episode for {patient.firstName} {patient.lastName}
        </DialogTitle>
        <DialogContent dividers sx={{ px: 3, py: 2, height: 470 }}>
          {episodeError ? (
            <Typography variant="body2" sx={{ color: "error.main" }}>
              Couldn't load this care episode. Please try again.
            </Typography>
          ) : (
            <>
              <Box sx={{ mb: 1 }}>
                <Skeleton variant="rounded" height={44} sx={{ width: "100%", maxWidth: 420 }} />
                <Skeleton variant="rounded" height={44} sx={{ width: "100%", maxWidth: 420, mt: 1 }} />
              </Box>

              <Box>
                <Box sx={{ mb: 1 }}>
                  <Skeleton variant="rounded" height={44} sx={{ width: "100%" }} />
                  <Box sx={{ mt: 1 }}>
                    {[0, 1, 2, 3].map((j) => (
                      <Skeleton
                        key={j}
                        variant="rounded"
                        height={36}
                        sx={{ width: "100%", ...(j === 0 ? {} : { mt: 1 }) }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>

              <Skeleton variant="rounded" height={36} sx={{ width: "100%", mt: 1 }} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose}>
            Cancel
          </Button>
          {episodeError && (
            <Button
              size="small"
              variant="text"
              onClick={() => refetch()}
              disabled={loading}
              sx={{ minWidth: 0, px: 1 }}
            >
              Retry
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  let initialEpisode: EpisodeFormState;
  const autoschedulerGroups = serviceLine.schedulingConfig.autoschedulerGroups;
  const activeOrganizationalUnits = organizationalUnits.filter((organizationalUnit) => organizationalUnit.active);
  if (isEditing && existingCareRequirements) {
    const tz = patient.addressTimezone || undefined;
    const { careRequirements: mapped, admissionDate, expectedDischargeDate } =
      parseCareRequirementsFromGQL(existingCareRequirements, tz);

    // Day-of-admission rows are flagged explicitly (dayOfAdmission); they're
    // pinned to a single-day window on the admission date for the editor.
    const admissionDayCRs = mapped
      .filter((cr) => cr.dayOfAdmission)
      .map((cr) => ({
        ...cr,
        dtstart: startOfDay(admissionDate),
        until: startOfDay(admissionDate),
      }));
    const ongoingCRs = mapped.filter((cr) => !cr.dayOfAdmission);
    const storedGroup = existingEpisode?.autoschedulerGroup;
    const autoschedulerGroup =
      storedGroup != null && storedGroup >= 1 && storedGroup <= autoschedulerGroups
        ? storedGroup
        : null;
    initialEpisode = {
      admissionDate,
      expectedDischargeDate,
      organizationalUnitId: existingEpisode?.organizationalUnitId ?? null,
      autoschedulerGroup,
      careRequirements: ongoingCRs,
      admissionDayCareRequirements: admissionDayCRs,
    };
  } else {
    const timezone = patient.addressTimezone || undefined;
    // Demo-only: when intake hands us an equipment ETA (defaultAdmitAfterMs),
    // default the admission date to the ETA's calendar day in the patient's
    // timezone — a "delivered tomorrow" ETA should admit tomorrow, not today.
    // The After time (time-of-day) is seeded separately in the form.
    const referenceDate = defaultAdmitAfterMs != null
      ? startOfDay(timezone ? toZonedTime(new Date(defaultAdmitAfterMs), timezone) : new Date(defaultAdmitAfterMs))
      : undefined;
    const { admissionDate, expectedDischargeDate } = getDefaultCareRequirementDateRange(
      referenceDate,
      timezone,
    );
    initialEpisode = {
      admissionDate,
      expectedDischargeDate,
      organizationalUnitId: activeOrganizationalUnits.length === 1
        ? activeOrganizationalUnits[0].id
        : null,
      autoschedulerGroup: null,
      careRequirements: [],
      admissionDayCareRequirements: [],
    };
  }

  return (
    <CareEpisodeDialogForm
      onClose={onClose}
      onSkip={onSkip}
      onSaved={onSaved}
      patient={patient}
      serviceLineId={serviceLineId}
      organizationalUnits={organizationalUnits}
      episodeId={episodeId}
      reopen={reopen}
      defaultTemplateName={defaultTemplateName}
      defaultAdmitAfterMs={defaultAdmitAfterMs}
      initialEpisode={initialEpisode}
      templates={templates}
      loadingTemplates={loadingTemplates}
      episodeActedOn={episodeActedOn}
    />
  );
};

export default CareEpisodeDialog
